import {
  DATE_PIPE_DEFAULT_OPTIONS,
  provideImageKitLoader,
} from '@angular/common';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  APP_ID,
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  isDevMode,
  LOCALE_ID,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  MAT_DATE_LOCALE,
  provideNativeDateAdapter,
} from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideNgIconsConfig } from '@ng-icons/core';
import { UI_DIALOG_EXPORT_COMPONENT } from '@seatix/control-hub/event-creation/utils';
import {
  CustomPaginatorIntl,
  provideApiUrl,
  provideBreakpoints,
  provideCdnUrl,
  provideEnvironment,
  provideIsTauri,
  provideSeatsioWorkspaceKey,
  provideStatusUrl,
  provideTurnstileSiteKey,
} from '@seatix/shared-config';
import { provideGoogleClientId } from '@seatix/shared-social-auth-feature';
import { provideAlertToast } from '@seatix/shared-util-alert';
import { provideAppVersion } from '@seatix/shared-util-app-version';
import { provideAppVersionRefreshHandler } from '@seatix/shared-util-app-version-refresh-handler';
import { provideLoadingDialog } from '@seatix/shared-util-loading';
import { provideThemeInitializer } from '@seatix/shared-util-theme-switcher';
import {
  coreProviders,
  ensureCsrfTokenInterceptor,
  errorInterceptor,
  errorNormalizationInterceptor,
  headersInterceptor,
  provideAppUrls,
} from '@seatix/shared/core';
import {
  authInterceptor,
  provideCrossAppLoginRedirect,
} from '@seatix/shared/data-access/auth';
import { provideSentry } from '@seatix/shared-util-error-tracking';
import { UiDialogExportComponent } from '@seatix/shared/ui/ui-dialog-export';
import {
  provideVersionInterceptorConfig,
  versionInterceptor,
} from '@seatix/util-http-version-interceptor';
import {
  provideTanStackQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { appRoutes } from './app.routes';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';
import { AppId } from '@seatix/shared-domain-app';

export const appConfig: ApplicationConfig = {
  providers: [
    ...coreProviders,
    provideCrossAppLoginRedirect(true),
    provideSentry(),
    provideAppVersionRefreshHandler(),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
    ),
    provideZonelessChangeDetection(),
    provideThemeInitializer(),
    provideBreakpoints(),
    provideNgIconsConfig({
      size: '24px',
    }),
    provideImageKitLoader(
      `https://ik.imagekit.io/seatix${
        process.env['STX_ENVIRONMENT'] === 'production'
          ? ''
          : `/${process.env['STX_ENVIRONMENT']}`
      }`,
    ),
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'es-MX' },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: MatPaginatorIntl,
      useClass: CustomPaginatorIntl,
    },
    provideHttpClient(
      withFetch(),
      withInterceptors([
        headersInterceptor,
        authInterceptor,
        versionInterceptor,
        ensureCsrfTokenInterceptor,
        errorNormalizationInterceptor,
        errorInterceptor,
      ]),
    ),
    {
      provide: APP_ID,
      useValue: process.env['STX_APP_ID'] as AppId,
    },
    { provide: LOCALE_ID, useValue: 'es-MX' },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: '$',
    },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { locale: 'es-MX' } },
    {
      provide: UI_DIALOG_EXPORT_COMPONENT,
      useValue: UiDialogExportComponent,
    },
    provideAlertToast(),
    provideAppVersion(process.env['STX_APP_VERSION'] || ''),
    provideVersionInterceptorConfig({
      headerName: `X-Seatix-Control-Hub-Desktop-Version`,
    }),
    provideTanStackQuery(
      new QueryClient(),
      ...(isDevMode() ? [withDevtools(() => ({ loadDevtools: true }))] : []),
    ),
    provideEnvironment(
      process.env['STX_ENVIRONMENT'] as
        'development' | 'staging' | 'production',
    ),
    provideAppUrls(
      process.env['STX_FAN_PORTAL_URL'] || '',
      process.env['STX_CONTROL_HUB_URL'] || '',
      process.env['STX_CONTROL_HUB_DESKTOP_URL'] || '',
      process.env['STX_CHECKOUT_URL'] || '',
    ),
    provideApiUrl(process.env['STX_API_URL'] || ''),
    provideCdnUrl(process.env['STX_CDN_URL'] || ''),
    provideStatusUrl(
      process.env['STX_STATUS_URL'] || 'https://status.seatix.com',
    ),
    provideSeatsioWorkspaceKey(process.env['STX_SEATSIO_WORKSPACE_KEY'] || ''),
    provideGoogleClientId(process.env['STX_GOOGLE_CLIENT_ID'] || ''),
    provideIsTauri(false),
    provideTurnstileSiteKey(
      process.env['STX_CLOUDFLARE_CAPTCHA_SITE_KEY'] || '',
    ),
    provideLoadingDialog(),
  ],
};
